import cx from 'classnames'
import {InputProvider} from 'components/common/CartItemBuilder/providers/InputProvider'
import {VentrataDataProvider} from 'components/common/CartItemBuilder/providers/VentrataDataProvider'
import {ErrorMessage} from 'components/common/ErrorMessage'
import {Warning} from 'components/system/Warning'
import {DATE_FORMAT, MONTH_FORMAT} from 'consts'
import {useGetCalendarQuery} from 'hooks/ventrata/useGetCalendarQuery'
import {map, max, range, sum, values} from 'lodash'
import moment from 'moment'
import {EnvironmentProvider} from 'providers/EnvironmentProvider'
import {useCallback, useMemo, useState} from 'react'
import {convertUnitCounterMapToRequestUnitCounters} from 'utils/convertUnitCounterMapToRequestUnitCounters'
import {ga4} from 'utils/ga4'
import {StepDataProvider} from '../../../../providers/StepDataProvider/StepDataProvider'
import {MonthSelector} from './MonthSelector'

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S']

const getInitialMonth = (date: string) => {
  return (date ? moment(date) : moment().startOf('month')).format(MONTH_FORMAT)
}

export const MaximizedCalendar = () => {
  const currency = EnvironmentProvider.useCurrency()
  const dataManager = StepDataProvider.useDateDataManager()
  const product = VentrataDataProvider.useProduct('optional')
  const unitCounters = InputProvider.useUnitCounters('optional')
  const totalUnitsCounter = StepDataProvider.guests.useTotalUnitsCounter()
  const ampleOptions = VentrataDataProvider.useAmpleOptions(totalUnitsCounter, 'optional')
  const optionalOptionId = EnvironmentProvider.useOptionalOptionId()
  const [month, setMonth] = useState(getInitialMonth(dataManager.getData()))
  const firstMonthDay = moment(month).day()
  const error = VentrataDataProvider.useError()
  const optionIds = useMemo(
    () => (optionalOptionId ? [optionalOptionId] : map(ampleOptions, 'id')),
    [optionalOptionId, ampleOptions]
  )

  const getCalendarQuery = useGetCalendarQuery(
    {
      productId: product.id,
      optionIds,
      localDateStart: moment(month).format(DATE_FORMAT),
      localDateEnd: moment(month).endOf('month').format(DATE_FORMAT),
      units: convertUnitCounterMapToRequestUnitCounters(unitCounters),
      currency,
    },
    {
      gcTime: 0,
      enabled: sum(values(unitCounters)) > 0,
      placeholderData: (prev) => prev,
    }
  )

  const onSelectDate = useCallback(
    (calendarDate: string) => {
      dataManager.save(calendarDate)

      ga4({
        event: 'select_date',
        label: calendarDate,
        item_id: product.id,
        item_name: product.title,
        checkout_type: 'custom_checkout',
      })
    },
    [product, dataManager]
  )

  if (error) {
    return <ErrorMessage error={error} />
  }

  if (!getCalendarQuery.data) {
    return null
  }

  return (
    <div className="flex flex-col">
      <MonthSelector month={month} setMonth={setMonth} />
      <div className="grid grid-cols-7 justify-items-center border-solid border-b border-b-gray-20 pt-2">
        {DAYS_OF_WEEK.map((day, i) => {
          return (
            <div key={i} className="text-gray-0 font-bold py-3">
              {day}
            </div>
          )
        })}
      </div>
      <div className="grid grid-cols-7 justify-items-center">
        {range(firstMonthDay).map((i) => {
          return <div key={i} />
        })}

        {range(0, moment(month).daysInMonth()).map((dayIndex) => {
          const calendarData = getCalendarQuery.data[dayIndex] || []
          const calendarDateMoment = moment(month).add(dayIndex, 'days')
          const calendarDate = calendarDateMoment.format(DATE_FORMAT)
          const available = calendarData.some((calendarDay) => calendarDay.available)

          return (
            <div
              key={calendarDate}
              className={cx('flex flex-col items-center w-full py-3 select-none text-lg', {
                'text-red-500 pointer-events-none line-through': !available,
                'cursor-pointer': available,
                'bg-theme rounded-lg text-white': dataManager.getData() === calendarDate,
              })}
              onClick={() => onSelectDate(calendarDate)}
            >
              <div>{calendarDateMoment.date()}</div>
            </div>
          )
        })}
      </div>
      {optionIds.length === 0 && (
        <Warning>
          Some dates are not available because you've exceeded the maximum number of guests for this tour (
          {max(map(product.options, 'restrictions.maxUnits'))})
        </Warning>
      )}
    </div>
  )
}
